import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Intent } from '@blueprintjs/core';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs } from 'components/navigation';
import { AlertUnsavedData, NotificationToaster } from 'components/notifications';
import { UserAccess } from 'components/user-access';
import { Wizard } from 'components/wizard';
import { HelperFunctions } from 'helpers';
import { useDirtyData } from 'hooks/useDirtyData';
import { useValidation } from 'hooks/useValidation';
import { PageRestricted } from 'pages/errors/page-restricted';
import {
    ReviewTypeDetailsStep,
    ReviewTypeSignOffStep,
    ReviewTypeSummaryStep,
    ReviewTypeTopicsStep,
    buildApiModel,
    buildDefaultReviewType,
    getDetailsValidationConfig,
    getSignOffValidationConfig,
    getTopicsValidationConfig,
    getTriggersValidationConfig
} from 'pages/employee-reviews/review-types/shared';
import { EmployeeReviewTypesService, EmployeeService } from 'services';

const requiredAction = "EmployeeReview:Manage";
const listingRoute = "/employee-reviews/review-types";
const badRequestCode = 400;

export function ReviewTypeCreate(props) {

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [employeeOptions, setEmployeeOptions] = useState({});
    const [reviewOptions, setReviewOptions] = useState({});
    const [reviewType, setReviewType] = useState(buildDefaultReviewType(null));
    const [originalReviewType, setOriginalReviewType] = useState(buildDefaultReviewType(null));
    const [wizardStep, setWizardStep] = useState(0);
    const [canProceed, setCanProceed] = useState(false);

    const [detailsValid, detailsErrors, validateDetails] = useValidation(false);
    const [topicsValid, topicsErrors, validateTopics] = useValidation(false);
    const [triggersValid, triggersErrors, validateTriggers] = useValidation(false);
    const [signOffValid, signOffErrors, validateSignOff] = useValidation(false);

    const isDataDirty = useDirtyData(originalReviewType, reviewType);
    const navigate = useNavigate();

    useEffect(() => {
        axios.all([
            EmployeeService.getEmployeeOptions(),
            EmployeeReviewTypesService.getEmployeeReviewOptions()
        ]).then(axios.spread(function (employeeOptionsResponse, reviewOptionsResponse) {
            setEmployeeOptions(employeeOptionsResponse);
            setReviewOptions(reviewOptionsResponse);

            const defaultReviewType = buildDefaultReviewType(reviewOptionsResponse);
            setReviewType(defaultReviewType);
            setOriginalReviewType(HelperFunctions.deepClone(defaultReviewType));
        })).catch(() => {
            NotificationToaster.show(Intent.DANGER, "Failed to get review type options. Please refresh the page.");
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (reviewType != null) {
            const details = getDetailsValidationConfig(reviewType, reviewOptions);
            validateDetails(details.rules, reviewType, details.externalRules);

            const topics = getTopicsValidationConfig(reviewType, reviewOptions);
            validateTopics(topics.rules, reviewType, topics.externalRules);

            const triggers = getTriggersValidationConfig(reviewType, reviewOptions);
            validateTriggers(triggers.rules, reviewType, triggers.externalRules);

            const signOff = getSignOffValidationConfig(reviewType, reviewOptions);
            validateSignOff(signOff.rules, reviewType, signOff.externalRules);
        }
    }, [reviewType, reviewOptions]);

    useEffect(() => {
        let stepValid;

        if (wizardStep === 0) {
            stepValid = detailsValid;
        } else if (wizardStep === 1) {
            stepValid = topicsValid && triggersValid;
        } else if (wizardStep === 2) {
            stepValid = signOffValid;
        } else {
            stepValid = detailsValid && topicsValid && triggersValid && signOffValid;
        }

        setCanProceed(!loading && !saving && stepValid);
    }, [wizardStep, detailsValid, topicsValid, triggersValid, signOffValid, loading, saving]);

    function onWizardChange(step) {
        setWizardStep(step);
    }

    function onReviewTypeUpdate(newReviewType) {
        setReviewType(newReviewType);
    }

    function handleSaveError(error) {
        if (error?.status === badRequestCode) {
            NotificationToaster.show(Intent.WARNING, error.data);
        } else {
            NotificationToaster.show(Intent.DANGER, "Failed to create review type. Please try again.");
        }
    }

    function onWizardFinish() {
        const model = buildApiModel(reviewType, reviewOptions);

        setSaving(true);

        EmployeeReviewTypesService.createReviewType(model).then(function () {
            NotificationToaster.show(Intent.SUCCESS, "Review type has been successfully created.");

            // Allow us to navigate away with no dirty data.
            setReviewType(originalReviewType);
            // Redirect at the end of the render cycle.
            setTimeout(() => {
                navigate(listingRoute);
            }, 100);
        }, handleSaveError).catch((error) => {
            handleSaveError(error);
        }).finally(() => {
            setSaving(false);
        });
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <div className="row">
                    <Breadcrumbs items={props.breadcrumbs} />
                    <h1>New Review Type</h1>
                    <Wizard
                        onStepChange={onWizardChange}
                        onFinish={onWizardFinish}
                        canProceed={canProceed}
                        disabled={saving}
                        finishButtonText="Create Review Type"
                        alignButtonsRight
                    >
                        <ReviewTypeDetailsStep
                            reviewType={reviewType}
                            onUpdate={onReviewTypeUpdate}
                            errors={detailsErrors}
                            employeeOptions={employeeOptions}
                            reviewOptions={reviewOptions}
                            loading={loading}
                            saving={saving}
                            createMode
                        />
                        <ReviewTypeTopicsStep
                            reviewType={reviewType}
                            onUpdate={onReviewTypeUpdate}
                            errors={topicsErrors}
                            triggerErrors={triggersErrors}
                            reviewOptions={reviewOptions}
                            loading={loading}
                            saving={saving}
                            createMode
                        />
                        <ReviewTypeSignOffStep
                            reviewType={reviewType}
                            onUpdate={onReviewTypeUpdate}
                            errors={signOffErrors}
                            reviewOptions={reviewOptions}
                            loading={loading}
                            saving={saving}
                        />
                        <ReviewTypeSummaryStep
                            reviewType={reviewType}
                            reviewOptions={reviewOptions}
                        />
                    </Wizard>
                    <AlertUnsavedData
                        isDirty={isDataDirty}
                    />
                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

ReviewTypeCreate.propTypes = {
    breadcrumbs: PropTypes.array
};
